import type {
  BuyerImportTabSummaryDto,
  BuyerSmartImportTabSelectionDto,
} from "@/api/buyerSmartImport";
import type { BuyerLeadSource } from "@/api/buyers";
import { FormField, Input } from "@/design-system/components/ui";
import { Select } from "@/components/ui/Select";
import { smartImportCopy } from "./smartImportCopy";
import { SmartImportMappingTable } from "./SmartImportMappingTable";
import { SMART_IMPORT_SOURCE_OPTIONS } from "./smartImportVocabulary";

/**
 * One tab of the file: whether to import it, where its header row is, what
 * Source its rows carry, and how every column maps (D4).
 *
 * <p>🔴 <b>Unticking a tab leaves it in the review state rather than removing
 * it.</b> A summary sheet arrives unticked and a lead tab can be unticked by
 * hand, and in both cases the operator may change their mind — so the mapping
 * is kept, and every consumer filters on `include` instead (`includedTabs`).
 * Dropping the tab would silently discard a mapping the model paid to
 * produce.</p>
 */

export interface SmartImportTabPanelProps {
  tab: BuyerSmartImportTabSelectionDto;
  /** The reader's own view of this tab, for the row counts and the samples. */
  summary?: BuyerImportTabSummaryDto;
  onToggleInclude: (include: boolean) => void;
  onChangeHeaderRow: (headerRowIndex: number) => void;
  onChangeSource: (source: BuyerLeadSource) => void;
  onChangeSourceDetail: (sourceDetail: string | null) => void;
  onChangeTarget: (columnIndex: number, target: string) => void;
  onChangeTransform: (columnIndex: number, transform: string) => void;
  onChangeSentinels: (columnIndex: number, sentinels: string[]) => void;
  onChangeFormat: (columnIndex: number, format: string) => void;
  disabled?: boolean;
}

export function SmartImportTabPanel({
  tab,
  summary,
  onToggleInclude,
  onChangeHeaderRow,
  onChangeSource,
  onChangeSourceDetail,
  onChangeTarget,
  onChangeTransform,
  onChangeSentinels,
  onChangeFormat,
  disabled,
}: SmartImportTabPanelProps) {
  return (
    <section
      aria-label={`Tab ${tab.tabName}`}
      className="border border-border-light rounded-md bg-white-pure p-space-3 grid gap-space-3"
      data-testid={`smart-import-tab-${tab.tabName}`}
    >
      <div className="flex flex-wrap items-baseline justify-between gap-space-2">
        <label className="flex items-center gap-space-2 text-body text-onyx">
          <input
            type="checkbox"
            data-testid={`tab-include-${tab.tabName}`}
            checked={tab.include}
            disabled={disabled}
            onChange={(e) => onToggleInclude(e.target.checked)}
          />
          <span className="font-medium">{tab.tabName}</span>
          <span className="text-caption text-gray-mid">{smartImportCopy.includeTabLabel}</span>
        </label>
        {summary && (
          <span className="text-caption text-gray-mid" data-testid={`tab-rows-${tab.tabName}`}>
            {summary.dataRowCount} rows · {summary.columnCount} columns
          </span>
        )}
      </div>

      {tab.include && (
        <>
          <div className="grid gap-space-3 md:grid-cols-2">
            {/*
              A SELECTOR over the raw rows, not a number box. The operator is
              being asked which row holds the column names, and they can only
              answer that by SEEING the rows — a spinner asks them to remember
              a row number off a spreadsheet they may not have open. The array
              arrives raw and uncut for exactly this reason, so choosing a
              different candidate costs no second upload.
            */}
            <FormField
              label={smartImportCopy.headerRowLabel}
              helperText={smartImportCopy.headerRowHint}
            >
              <Select
                data-testid={`tab-header-row-${tab.tabName}`}
                disabled={disabled}
                value={String(tab.headerRowIndex)}
                onChange={(e) => onChangeHeaderRow(Number.parseInt(e.target.value, 10))}
              >
                {headerRowOptions(summary?.sampleRows, tab.headerRowIndex).map((o) => (
                  <option key={o.rowNumber} value={o.rowNumber}>
                    {o.label}
                  </option>
                ))}
              </Select>
            </FormField>

            <FormField
              label={smartImportCopy.tabSourceLabel}
              helperText={smartImportCopy.tabSourceHint}
            >
              <Select
                data-testid={`tab-source-${tab.tabName}`}
                disabled={disabled}
                value={tab.source}
                onChange={(e) => onChangeSource(e.target.value as BuyerLeadSource)}
              >
                {SMART_IMPORT_SOURCE_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </Select>
            </FormField>
          </div>

          {/*
            Editable, not merely carried. `ListingSite` on its own does not say
            WHICH site, and this is the only place the answer can be corrected:
            the compose step receives the review state and never the plan, so a
            suggestion left unedited here is the record for every buyer in the
            tab.
          */}
          <FormField
            label={smartImportCopy.tabSourceDetailLabel}
            helperText={smartImportCopy.tabSourceDetailHint}
          >
            <Input
              data-testid={`tab-source-detail-${tab.tabName}`}
              disabled={disabled}
              placeholder={smartImportCopy.tabSourceDetailPlaceholder}
              value={tab.sourceDetail ?? ""}
              onChange={(e) => onChangeSourceDetail(e.target.value.trim() ? e.target.value : null)}
            />
          </FormField>

          <SmartImportMappingTable
            tab={tab}
            sampleRows={summary?.sampleRows}
            onChangeTarget={onChangeTarget}
            onChangeTransform={onChangeTransform}
            onChangeSentinels={onChangeSentinels}
            onChangeFormat={onChangeFormat}
            disabled={disabled}
          />
          <p className="text-caption text-gray-mid">{smartImportCopy.sentinelsHint}</p>
        </>
      )}
    </section>
  );
}

/** How many raw rows the header-row selector offers — the server's own cap. */
export const HEADER_ROW_CANDIDATE_CAP = 20;

/** How much of a row is shown on its option, so the label stays readable. */
const HEADER_ROW_PREVIEW_CELLS = 6;
const HEADER_ROW_PREVIEW_CHARS = 80;

export interface HeaderRowOption {
  /** 1-based RAW row number, which is what `headerRowIndex` holds. */
  rowNumber: number;
  label: string;
}

/**
 * The rows the header-row selector offers.
 *
 * <p>🔴 <b>The CURRENT row is always offered, even when the samples do not
 * reach it.</b> The array is capped at 20 and a plan may legitimately name a
 * later row; a `<select>` whose value matches no option renders blank, so
 * omitting it would show an empty control on a tab that is mapped perfectly
 * well — and the first change to any other field would then silently rewrite
 * the header row to whatever the browser had selected instead.</p>
 */
export function headerRowOptions(
  sampleRows: readonly string[][] | null | undefined,
  current: number,
): HeaderRowOption[] {
  const rows = sampleRows ?? [];
  const options: HeaderRowOption[] = rows
    .slice(0, HEADER_ROW_CANDIDATE_CAP)
    .map((row, i) => ({ rowNumber: i + 1, label: headerRowLabel(i + 1, row) }));

  if (!options.some((o) => o.rowNumber === current)) {
    options.push({ rowNumber: current, label: `Row ${current}` });
    options.sort((a, b) => a.rowNumber - b.rowNumber);
  }
  // A tab whose samples came back empty still needs something to select.
  return options.length > 0 ? options : [{ rowNumber: current, label: `Row ${current}` }];
}

function headerRowLabel(rowNumber: number, row: readonly string[]): string {
  const cells = row
    .slice(0, HEADER_ROW_PREVIEW_CELLS)
    .map((c) => c.trim())
    .filter((c) => c.length > 0)
    .join(" · ");
  const preview =
    cells.length > HEADER_ROW_PREVIEW_CHARS
      ? `${cells.slice(0, HEADER_ROW_PREVIEW_CHARS - 1)}…`
      : cells;
  return preview ? `Row ${rowNumber}: ${preview}` : `Row ${rowNumber} (blank)`;
}
